import { ModalBaseComponent } from '@/components/base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '@/components/doc-add-widget/doc-add-widget.component';
import { InputComponent } from '@/components/ui-forms/input/input.component';
import { SelectComponent } from '@/components/ui-forms/select/select.component';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterContentChecked, AfterViewChecked, Component, Inject, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  DocumentSummary,
  EnergyClassification,
  PropertiesBookletService,
  PropertyBookletCertification,
  PropertyBookletEquipmentDetail,
  PropertyBookletEquipmentMaintenance,
  PropertyBookletEquipmentNetworkDetail,
  PropertyBookletEquipments,
  PropertyBookletInsulation,
  PropertyBookletInsulationDetail,
  PropertyBookletInsulationWindowDetail,
  RealProperty
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PropertyBookletInsulationType } from '../property-booklet-insulation-card/property-booklet-insulation-card.component';
import { PropertyBookletInsulationService } from '../property-booklet-insulation-card/property-booklet-insulation.service';
import { PropertyBookletEquipmentType } from '../property-booklet-equipment-card/property-booklet-equipment-card.component';
import { PropertyBookletEquipmentService } from '../property-booklet-equipment-card/property-booklet-equipment.service';
import { PropertyBookletCertificationType } from '../property-booklet-certification-card/property-booklet-certification-card.component';
import { PropertyBookletCertificationService } from '../property-booklet-certification-card/property-booklet-certification.service';

export class PropertyBookletCertificationDialogData {
  prop: RealProperty;
  bookletUid: string;
  type: PropertyBookletCertificationType;
}

@Component({
  selector: 'app-property-booklet-certification-dialog',
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgIf,
    NgTemplateOutlet,
    MatDialogModule,
    ReactiveFormsModule,
    DocAddWidgetComponent,
    InputComponent,
    SelectComponent,
    MatFormFieldModule
  ],
  templateUrl: './property-booklet-certification-dialog.component.html',
  styleUrl: './property-booklet-certification-dialog.component.css'
})
export class PropertyBookletCertificationDialogComponent {

  private readonly propertiesBookletService = inject(PropertiesBookletService);

  bookletUid: string;
  prop: RealProperty;
  sub = new Subscription();
  arePopulateData = false;

  title: string;
  type: PropertyBookletCertificationType
  isLoading = false;
  PropertyBookletCertificationType = PropertyBookletCertificationType;
  energyClassificationList: EnergyClassification[] = Object.values(EnergyClassification);


  // energy
  get form_propertyTaxId() {
    return this.form.get('propertyTaxId') as FormControl<string>;
  }
  get form_energyDiagnosticDate() {
    return this.form.get('energyDiagnosticDate') as FormControl<string>;
  }
  get form_energyDiagnosticId() {
    return this.form.get('energyDiagnosticId') as FormControl<string>;
  }
  get form_energyConsumptionClassification() {
    return this.form.get('energyConsumptionClassification') as FormControl<EnergyClassification>;
  }
  get form_greenhouseGasClassification() {
    return this.form.get('greenhouseGasClassification') as FormControl<EnergyClassification>;
  }
  get form_energyDiagnosticDocument() {
    return this.form.get('energyDiagnosticDocument') as FormControl<DocumentSummary>;
  }
  get form_energyAuditDate() {
    return this.form.get('energyAuditDate') as FormControl<string>;
  }
  get form_energyAuditDocument() {
    return this.form.get('energyAuditDocument') as FormControl<DocumentSummary>;
  }

  // quality
  get form_qualitySealDate() {
    return this.form.get('qualitySealDate') as FormControl<string>;
  }
  get form_qualitySealDocument() {
    return this.form.get('qualitySealDocument') as FormControl<DocumentSummary>;
  }

  // re2020
  get form_re2020SealDocument() {
    return this.form.get('re2020SealDocument') as FormControl<DocumentSummary>;
  }

  form = this.fb.group({
    // energy
    propertyTaxId: [null],
    energyDiagnosticDate: [null],
    energyDiagnosticId: [null],
    energyConsumptionClassification: [null],
    greenhouseGasClassification: [null],
    energyDiagnosticDocument: [null],
    energyAuditDate: [null],
    energyAuditDocument: [null],
    // quality
    qualitySealDate: [null],
    qualitySealDocument: [null],
    // re2020
    re2020SealDocument: [null],

  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyBookletCertificationDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PropertyBookletCertificationDialogComponent>,
  ) {
    this.prop = data.prop;
    this.bookletUid = data.bookletUid;
    this.type = data.type;
    this.title = PropertyBookletCertificationService.getTitle(data.type);

    this.initForm();
  }

  initForm() {
    this.populateData()
  }

  setValidators() {
    if (this.type === PropertyBookletCertificationType.energy) {
      this.form_propertyTaxId.addValidators(Validators.required);
    }
  }

  populateData() {
    this.isLoading = true;

    this.sub = this.propertiesBookletService.getRealPropertyBookletCertification(this.prop.uid, this.bookletUid).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.setFormFromData(resp);
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.arePopulateData = true;
      }
    });
  }

  setFormFromData(resp: PropertyBookletCertification) {
    this.setValidators();
    this.form_propertyTaxId.setValue(resp?.propertyTaxId ?? null);
    this.form_energyDiagnosticDate.setValue(resp?.energyDiagnosticDate ?? null);
    this.form_energyDiagnosticId.setValue(resp?.energyDiagnosticId ?? null);
    this.form_energyConsumptionClassification.setValue(resp?.energyConsumptionClassification ?? null);
    this.form_greenhouseGasClassification.setValue(resp?.greenhouseGasClassification ?? null);
    this.form_energyDiagnosticDocument.setValue(resp?.energyDiagnosticDocument ?? null);
    this.form_energyAuditDate.setValue(resp?.energyAuditDate ?? null);
    this.form_energyAuditDocument.setValue(resp?.energyAuditDocument ?? null);
    this.form_qualitySealDate.setValue(resp?.qualitySealDate ?? null);
    this.form_qualitySealDocument.setValue(resp?.qualitySealDocument ?? null);
    this.form_re2020SealDocument.setValue(resp?.re2020SealDocument ?? null);  
  }

  onEnergyDiagnosticDocumentDocSelected(newDoc: DocumentSummary) {
    this.form_energyDiagnosticDocument.setValue(newDoc)
  }

  onEnergyDiagnosticDocumentDocDeleted() {
    this.form_energyDiagnosticDocument.setValue(null);
  }

  onEnergyAuditDocumentDocSelected(newDoc: DocumentSummary) {
    this.form_energyAuditDocument.setValue(newDoc)
  }

  onEnergyAuditDocumentDocDeleted() {
    this.form_energyAuditDocument.setValue(null);
  }

  onQualitySealDocumentDocSelected(newDoc: DocumentSummary) {
    this.form_qualitySealDocument.setValue(newDoc)
  }

  onQualitySealDocumentDocDeleted() {
    this.form_qualitySealDocument.setValue(null);
  }

  onRe2020SealDocumentDocSelected(newDoc: DocumentSummary) {
    this.form_re2020SealDocument.setValue(newDoc)
  }

  onRe2020SealDocumentDocDeleted() {
    this.form_re2020SealDocument.setValue(null);
  }

  onSubmit() {
    if (this.form.valid) {
      this.update();
    }
  }

  update() {
    this.isLoading = true;
    const body = this.getBody();
    this.sub = this.propertiesBookletService.updateRealPropertyBookletCertification(this.prop.uid, this.bookletUid, body).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }
  getBody(): PropertyBookletCertification {
    const body: PropertyBookletCertification = {
      propertyTaxId: this.form_propertyTaxId.value ?? null
    };

    if (this.type == PropertyBookletCertificationType.energy) {
      body.energyDiagnosticDate = this.form_energyDiagnosticDate.value ?? null;
      body.energyDiagnosticId = this.form_energyDiagnosticId.value ?? null;
      body.energyConsumptionClassification = this.form_energyConsumptionClassification.value ?? null;
      body.greenhouseGasClassification = this.form_greenhouseGasClassification.value ?? null;
      body.energyDiagnosticDocument = this.form_energyDiagnosticDocument.value ?? null;
      body.energyAuditDate = this.form_energyAuditDate.value ?? null;
      body.energyAuditDocument = this.form_energyAuditDocument.value ?? null;
    } else if (this.type == PropertyBookletCertificationType.quality) {
      body.qualitySealDate = this.form_qualitySealDate.value ?? null;
      body.qualitySealDocument = this.form_qualitySealDocument.value ?? null;      
    } else {      
      body.re2020SealDocument = this.form_re2020SealDocument.value ?? null;  
    } 
    
    return body;
  }
}
